import * as React from 'react';
import { Button, Description, Modal } from '@heroui/react';
import { BasicInfoSection } from './BasicInfoSection';
import { QuestionDesignSection } from './QuestionDesignSection';
import { SettingsSection } from './SettingsSection';
import { formDataToSchema } from './questionnaire-schema';
import { useQuestionnaireFormState } from './useQuestionnaireFormState';
import type { QuestionnaireSchema, QuestionnaireType, Question, QuestionnaireFormData } from './types';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export interface QuestionnaireFormDialogQuestionnaire {
  /** tRPC + SuperJSON 可能为 bigint */
  id?: string | bigint | number;
  title?: string;
  description?: string | null;
  type?: string;
  isShared?: boolean;
  schemaJson?: Record<string, unknown> | null;
}

export interface QuestionnaireFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionnaire: QuestionnaireFormDialogQuestionnaire | null;
  loading?: boolean;
  onSubmit: (payload: {
    title: string;
    description?: string;
    type: QuestionnaireType;
    is_shared: boolean;
    schema_json: QuestionnaireSchema;
    formData: QuestionnaireFormData;
    structureBaseline: QuestionnaireFormData | null;
  }) => void | Promise<void>;
}

const FORM_ID = 'questionnaire-form-dialog';

export function QuestionnaireFormDialog({
  open,
  onOpenChange,
  questionnaire,
  loading = false,
  onSubmit,
}: QuestionnaireFormDialogProps) {
  const {
    form,
    setTitle,
    setDescription,
    setType,
    setIsShared,
    setUseSections,
    setSettings,
    addSection,
    removeSection,
    updateSection,
    addQuestion,
    removeQuestion,
    addQuestionToSection,
    removeQuestionFromSection,
    updateQuestion,
    reset,
    loadFromQuestionnaire,
    getFormData,
    getStructureBaseline,
  } = useQuestionnaireFormState();

  /** 避免父组件每次渲染传入新 questionnaire 引用时重复 load，把表单与 baseline 冲回服务端快照，导致 diff 为空、选项保存失败 */
  const lastLoadedEditKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      lastLoadedEditKeyRef.current = null;
      return;
    }
    if (!questionnaire) {
      lastLoadedEditKeyRef.current = null;
      reset();
      return;
    }
    const editKey = questionnaire.id != null ? String(questionnaire.id) : '';
    if (lastLoadedEditKeyRef.current === editKey && editKey !== '') {
      return;
    }
    lastLoadedEditKeyRef.current = editKey;
    loadFromQuestionnaire({
      title: questionnaire.title,
      description: questionnaire.description,
      type: questionnaire.type,
      isShared: questionnaire.isShared,
      schemaJson: questionnaire.schemaJson ?? null,
    });
  }, [open, questionnaire, loadFromQuestionnaire, reset]);

  const handleClose = React.useCallback(() => {
    reset();
    onOpenChange(false);
  }, [reset, onOpenChange]);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) handleClose();
    },
    [handleClose]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || loading) return;
    const fd = getFormData();
    const schema = formDataToSchema(fd);
    await onSubmit({
      title: fd.title.trim(),
      description: fd.description?.trim() || undefined,
      type: fd.type,
      is_shared: fd.isShared,
      schema_json: schema,
      formData: fd,
      structureBaseline: getStructureBaseline(),
    });
    handleClose();
  };

  const isEdit = Boolean(questionnaire?.id);

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="full" className="max-w-4xl">
          <Modal.Dialog className="max-h-[min(90vh,42rem)]">
            <Modal.Header className="shrink-0 gap-1">
              <Modal.Heading>{isEdit ? '编辑问卷' : '新建问卷'}</Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改问卷配置信息' : '创建新的问卷，支持多种问卷类型与题目结构'}
              </Description>
            </Modal.Header>
            <Modal.Body className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-8 pb-2">
                <section className="space-y-4">
                  <h3 className="text-lg font-semibold">基础信息</h3>
                  <BasicInfoSection
                    title={form.title}
                    description={form.description}
                    type={form.type}
                    isShared={form.isShared}
                    disabled={loading}
                    onTitleChange={setTitle}
                    onDescriptionChange={setDescription}
                    onTypeChange={setType}
                    onIsSharedChange={setIsShared}
                  />
                </section>

                <section className="space-y-4">
                  <QuestionDesignSection
                    useSections={form.useSections}
                    sections={form.sections}
                    questions={form.questions}
                    questionnaireType={form.type}
                    disabled={loading}
                    onUseSectionsChange={setUseSections}
                    onAddSection={addSection}
                    onRemoveSection={removeSection}
                    onUpdateSection={updateSection}
                    onAddQuestion={(q: Question, sectionId?: string) => {
                      if (sectionId) addQuestionToSection(sectionId, q);
                      else addQuestion(q);
                    }}
                    onRemoveQuestion={(questionId, sectionId) => {
                      if (sectionId) removeQuestionFromSection(sectionId, questionId);
                      else removeQuestion(questionId);
                    }}
                    onUpdateQuestion={(questionId, updates, sectionId) => updateQuestion(questionId, updates, sectionId)}
                  />
                </section>

                <section className="space-y-4">
                  <h3 className="text-lg font-semibold">问卷设置</h3>
                  <SettingsSection settings={form.settings} disabled={loading} onSettingsChange={setSettings} />
                </section>
              </form>
            </Modal.Body>
            <Modal.Footer className="shrink-0">
              <Button type="button" variant="ghost" onPress={handleClose} isDisabled={loading}>
                取消
              </Button>
              <Button type="submit" form={FORM_ID} variant="primary" isDisabled={loading}>
                {isEdit ? '保存' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
